import React, { useState } from "react";
import { Button, Card, Typography, List, message, Space } from "antd";
import { PlusOutlined, CheckOutlined } from "@ant-design/icons";
import { useHRAPI } from "../../hooks/useHRAPI";
import type { ContractTypeFormData } from "../../types/hr";

const { Title, Text } = Typography;

interface DefaultContractTypesHelperProps {
  onSuccess?: () => void;
}

const DefaultContractTypesHelper: React.FC<DefaultContractTypesHelperProps> = ({
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const { createContractType } = useHRAPI();

  const defaultContractTypes: ContractTypeFormData[] = [
    {
      name: "CDI",
      code: "CDI",
      description:
        "Contrat à Durée Indéterminée - Contrat de travail permanent sans limitation de durée",
      max_duration_months: undefined, // Durée illimitée
      renewable: false, // Un CDI n'est pas renouvelable
      trial_period_days: 90, // 3 mois de période d'essai standard
      notice_period_days: 30, // 1 mois de préavis
      is_active: true,
    },
    {
      name: "CDD",
      code: "CDD",
      description:
        "Contrat à Durée Déterminée - Contrat temporaire avec une date de fin fixée",
      max_duration_months: 24, // Maximum 2 ans pour un CDD
      renewable: true, // Un CDD peut être renouvelé
      trial_period_days: 30, // 1 mois de période d'essai
      notice_period_days: 15, // 15 jours de préavis
      is_active: true,
    },
    {
      name: "Stage",
      code: "STAGE",
      description:
        "Convention de Stage - Période de formation pratique en entreprise",
      max_duration_months: 6, // 6 mois maximum pour un stage
      renewable: true, // Un stage peut être prolongé
      trial_period_days: 0, // Pas de période d'essai pour un stage
      notice_period_days: 7, // 1 semaine de préavis
      is_active: true,
    },
    {
      name: "Consultance",
      code: "CONS",
      description:
        "Contrat de Consultance - Prestation de services spécialisés",
      max_duration_months: 12, // 1 an maximum
      renewable: true, // Renouvelable
      trial_period_days: 0, // Pas de période d'essai
      notice_period_days: 30, // 1 mois de préavis
      is_active: true,
    },
    {
      name: "Freelance",
      code: "FREE",
      description:
        "Travailleur Indépendant - Collaboration en tant qu'indépendant",
      max_duration_months: undefined, // Durée variable selon le projet
      renewable: true, // Renouvelable par projet
      trial_period_days: 0, // Pas de période d'essai
      notice_period_days: 15, // 15 jours de préavis
      is_active: true,
    },
  ];

  const handleCreateDefaults = async () => {
    setLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const contractType of defaultContractTypes) {
        try {
          await createContractType(contractType);
          successCount++;
        } catch (error) {
          console.error(
            `Erreur lors de la création de ${contractType.name}:`,
            error
          );
          errorCount++;
        }
      }

      if (successCount > 0) {
        message.success(
          `${successCount} type(s) de contrat créé(s) avec succès${
            errorCount > 0 ? ` (${errorCount} erreur(s))` : ""
          }`
        );
        onSuccess?.();
      } else {
        message.error("Aucun type de contrat n'a pu être créé");
      }
    } catch (error) {
      console.error("Erreur générale:", error);
      message.error(
        "Erreur lors de la création des types de contrats par défaut"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      style={{ marginBottom: 24 }}
      title={
        <Title level={4} style={{ margin: 0, color: "#1890ff" }}>
          Créer des types de contrats par défaut
        </Title>
      }
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={loading}
          onClick={handleCreateDefaults}
        >
          Créer les types par défaut
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <Text>
          Pour commencer rapidement, vous pouvez créer les types de contrats les
          plus couramment utilisés. Ces types peuvent être modifiés
          ultérieurement selon vos besoins.
        </Text>

        <List
          size="small"
          bordered
          dataSource={defaultContractTypes}
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta
                avatar={<CheckOutlined style={{ color: "#52c41a" }} />}
                title={
                  <Space>
                    <strong>{item.name}</strong>
                    {item.code && <Text type="secondary">({item.code})</Text>}
                  </Space>
                }
                description={
                  <div>
                    <div>{item.description}</div>
                    <div
                      style={{ marginTop: 4, fontSize: "12px", color: "#666" }}
                    >
                      Durée max:{" "}
                      {item.max_duration_months
                        ? `${item.max_duration_months} mois`
                        : "Illimitée"}{" "}
                      • Période d'essai:{" "}
                      {item.trial_period_days > 0
                        ? `${item.trial_period_days} jours`
                        : "Aucune"}{" "}
                      • Préavis: {item.notice_period_days} jours • Renouvelable:{" "}
                      {item.renewable ? "Oui" : "Non"}
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Space>
    </Card>
  );
};

export default DefaultContractTypesHelper;
